/**
 * useResolvedField — fetch + cache resolved form schema (sau merge 4-layer).
 *
 * Dùng cho FE preview hoặc page personal override khi cần thấy "schema cuối"
 * mà user thực tế thấy.
 */
import * as React from "react";

import { formsApi, type ResolvedFormSchema } from "@/api/forms";

interface State {
  schema: ResolvedFormSchema | null;
  loading: boolean;
  error: string | null;
}

const cache = new Map<string, Promise<ResolvedFormSchema>>();

export function useResolvedSchema(
  formCode: string | null,
  version?: number,
): State & { reload: () => void } {
  const [state, setState] = React.useState<State>({
    schema: null,
    loading: !!formCode,
    error: null,
  });
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    if (!formCode) {
      setState({ schema: null, loading: false, error: null });
      return;
    }
    setState({ schema: null, loading: true, error: null });
    const cacheKey = `${formCode}@${version ?? "current"}`;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        let promise = cache.get(cacheKey);
        if (!promise || tick > 0) {
          promise = formsApi.getResolved(formCode, version);
          cache.set(cacheKey, promise);
        }
        const schema = await promise;
        if (cancelled) return;
        setState({ schema, loading: false, error: null });
      } catch (e) {
        if (cancelled) return;
        setState({
          schema: null,
          loading: false,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [formCode, version, tick]);

  return {
    ...state,
    reload: () => {
      if (formCode) cache.delete(`${formCode}@${version ?? "current"}`);
      setTick((t) => t + 1);
    },
  };
}
